import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useTransitionNavigate } from '../context/TransitionContext';
import FloatingHeader from '../components/layout/FloatingHeader';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import Produktseite from '../components/produktseite/Produktseite';
import { titelPfad } from '../components/produktseite/Kopf';
import { useCart } from '../context/CartContext';
import NotFound from './NotFound';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';
import KaufHinweis, { gemerkteKaufSprache } from '../components/kauf/KaufHinweis';

// Produktseiten im neuen Design (Entwurf ~/Projects/davidmedia-header-test/produkt.html, 04.10.2026).
// Hier bleiben: Produktdaten (Preis, Bilder, Downloads), Umleitungen alter Adressen, Strukturdaten für
// Google und die Kauf-Logik. Aufbau und Texte der Seite: components/produktseite + data/produktSeiten.js.

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Bilder liegen als "/products/…" vor, damit sie hinter jeder Domain und
   lokal funktionieren. Fuer Strukturdaten und Link-Vorschauen ist das aber
   wertlos: Google und die Messenger holen das Bild von ihren eigenen Servern
   und koennen eine relative Adresse nicht aufloesen. */
const absUrl = (u) =>
  (u && !/^https?:\/\//.test(u) ? `https://www.davidmedia.de${u.startsWith('/') ? '' : '/'}${u}` : u);

// Product data
const productsData = {
  // Editor Suite bundle — all three plugins, one licence key. product_id 'all'
  // routes through the same checkout as a single product; the backend issues an
  // 'all' Suite key and the delivery paths expand it into the three plugins.
  'studio-flow': {
    id: 'studio-flow',
    name: 'Studio Flow',
    // Studio Flow gibt es nur im All-in-One-Abo — es ist ein Produkt IM Abo,
    // nicht das Abo selbst (das hat seine eigene Seite: /shop/all-in-one).
    // Der Knopf fuehrt dorthin statt in den Warenkorb.
    price: 'All-in-One',
    priceValue: 159.00,
    aboProdukt: true,
    // Galerie: Titelbild + echte UI-Screenshots (statt Videos). Die Screenshots
    // erzeugt ae-plugins/GraphFlow/make-shots.sh aus dem laufenden Plugin —
    // dieselbe Screen-Liste, aus der auch das UI-Kit baut. Nicht von Hand
    // pflegen: Skript laufen lassen, dann stimmen Bild und Stand ueberein.
    // Das Titelbild lag identisch schon lokal; der alte CDN-Hotlink war Altlast.
    images: [
      '/products/titel/studio-flow.jpg',
      '/products/studio-flow/01-kurven-wert.webp',
      '/products/studio-flow/02-kurven-geschwindigkeit.webp',
      '/products/studio-flow/03-kurven-vorlagen.png',
      '/products/studio-flow/05-bounce.webp',
      '/products/studio-flow/06-wiggle.webp',
      '/products/studio-flow/09-transitions.png',
      '/products/studio-flow/10-animation.webp',
      '/products/studio-flow/11-text-animation.webp',
      '/products/studio-flow/12-text-box.png',
      '/products/studio-flow/13-grid.png',
      '/products/studio-flow/14-karussell.png',
      '/products/studio-flow/15-formen.png',
      '/products/studio-flow/16-uikit-elemente.png',
      '/products/studio-flow/17-uikit-screens.png',
      '/products/studio-flow/18-hintergruende.png',
      '/products/studio-flow/19-workflow.png',
      '/products/studio-flow/20-eigene-expressions.png',
      '/products/studio-flow/21-einstellungen.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    // Paid product: handleDownload only fires for priceValue === 0, so this
    // field was never read. It still pointed at an outdated .zxp on the old
    // build CDN — delivery runs through the licensed library instead.
    downloadUrl: null
  },
  'flow-panel': {
    id: 'flow-panel',
    name: 'Flow Panel',
    price: 'Free',
    priceValue: 0,
    isFree: true,
    images: [
      '/products/titel/flow-panel.jpg',
      '/products/flowpanel-panels.png',
      '/products/flowpanel-liste.png'
    ],
    version: '1.0.0',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    // Der Download kommt, sobald die Datei im Shop liegt.
    downloadUrl: null
  },
  'instant-curve': {
    id: 'instant-curve',
    name: 'Instant Curve',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/instant-curve.jpg',
      '/products/studio-flow/01-kurven-wert.webp',
      '/products/studio-flow/02-kurven-geschwindigkeit.webp',
      '/products/studio-flow/03-kurven-vorlagen.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'bounce-flow': {
    id: 'bounce-flow',
    name: 'Bounce Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/bounce-flow.jpg',
      '/products/studio-flow/05-bounce.webp'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'shake-flow': {
    id: 'shake-flow',
    name: 'Shake Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/shake-flow.jpg',
      '/products/studio-flow/06-wiggle.webp'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'glitch-flow': {
    id: 'glitch-flow',
    name: 'Glitch Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/glitch-flow.jpg'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'transition-flow': {
    id: 'transition-flow',
    name: 'Transition Flow',
    // Nicht mehr einzeln: braucht Shake/Animation/Glitch-Vorlagen der anderen
    // Werkzeuge. Nur in Studio Flow / All-in-One — Knopf fuehrt dorthin.
    price: 'All-in-One',
    priceValue: 14.99,
    aboProdukt: true,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/transitions.png',
      '/products/studio-flow/09-transitions.png',
      '/products/studio-flow/10-animation.webp'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'text-flow': {
    id: 'text-flow',
    name: 'Text Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/text-flow.jpg',
      '/products/studio-flow/11-text-animation.webp',
      '/products/studio-flow/12-text-box.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'grid-flow': {
    id: 'grid-flow',
    name: 'Grid Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/grid-flow.jpg',
      '/products/studio-flow/13-grid.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'carousel-flow': {
    id: 'carousel-flow',
    name: 'Carousel Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/carousel-flow.jpg',
      '/products/studio-flow/14-karussell.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'shape-flow': {
    id: 'shape-flow',
    name: 'Shape Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/shape-flow.jpg',
      '/products/studio-flow/15-formen.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'background-flow': {
    id: 'background-flow',
    name: 'Background Flow',
    price: '€14.99',
    priceValue: 14.99,
    // Modul von Studio Flow: ausgeliefert wird die eine Erweiterung, die Lizenz
    // entscheidet, welche Bereiche offen sind. Darum kein eigener Download.
    modulVon: 'studio-flow',
    images: [
      '/products/titel/background-flow.jpg',
      '/products/studio-flow/18-hintergruende.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'instant-ui-kit': {
    id: 'instant-ui-kit',
    name: 'Instant UI Kit',
    price: '€14.99',
    priceValue: 14.99,
    modulVon: 'studio-flow',
    images: [
      '/products/titel/instant-ui-kit.jpg',
      '/products/studio-flow/16-uikit-elemente.png',
      '/products/studio-flow/17-uikit-screens.png'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'instant-comp': {
    id: 'instant-comp',
    name: 'Instant Comp',
    price: '€24.99',
    priceValue: 24.99,
    modulVon: 'studio-flow',
    images: [
      '/products/titel/instant-comp.jpg'
    ],
    version: '1.1.15',
    releaseDate: 'September 2026',
    releaseDateDE: 'September 2026',
    downloadUrl: null
  },
  'media-flow': {
    id: 'media-flow',
    name: 'Media Flow',
    price: '€24.99',
    priceValue: 24.99,
    // Galerie: Titelbild + echte UI-Screenshots (statt Videos).
    images: [
      '/products/mediaflow.png',
      '/products/media-flow/footage.png',
      '/products/media-flow/grafik.png',
      '/products/media-flow/sounds.png',
      '/products/media-flow/filters.png',
      '/products/media-flow/search.png',
      '/products/media-flow/favorites.png',
      '/products/media-flow/settings.png',
      '/products/media-flow/tutorial.png'
    ],
    version: '1.0.3',
    releaseDate: 'January 2025',
    releaseDateDE: 'Januar 2025',
    // Same as Instant Curve: unused field that still pointed at an old CDN .zxp.
    downloadUrl: null
  },
  'effect-flow': {
    id: 'effect-flow',
    name: 'Effect Flow',
    price: null,
    priceValue: 0,
    comingSoon: true,
    images: [
      '/products/effectflow.png'
    ],
    version: '2.0.0',
    releaseDate: 'December 2024',
    releaseDateDE: 'Dezember 2024',
    downloadUrl: null
  },
  // Instant Grade (bis Oktober 2026 Instant Grade) — Farbkorrektur fuer After Effects.
  // Now live & buyable (€24.99). Download delivered post-purchase via the
  // update-push system, so downloadUrl stays null here.
  'instant-grade': {
    id: 'instant-grade',
    name: 'Instant Grade',
    price: '€24.99',
    priceValue: 24.99,
    // Galerie: Titelbild + echte UI-Screenshots (statt Videos).
    images: [
      '/products/titel/instant-grade.jpg',
      '/products/look-flow/01-looks.png',
      '/products/look-flow/02-looks-cinematic.png',
      '/products/look-flow/03-preview.png',
      '/products/look-flow/04-builder.png',
      '/products/look-flow/05-wheels.png',
      '/products/look-flow/06-curves.png',
      '/products/look-flow/07-palette.png',
      '/products/look-flow/08-tutorial.png',
      '/products/look-flow/09-lut.png'
    ],
    version: '1.0.4',
    releaseDate: 'June 2026',
    releaseDateDE: 'Juni 2026',
    downloadUrl: null
  },
  'text-animations': {
    id: 'text-animations',
    name: '5+ Text Animations',
    price: 'Free',
    priceValue: 0,
    isFree: true,
    images: [
      '/products/titel/text-animations.jpg',
      { type: 'video', url: '/products/text-animations-preview.mp4' }
    ],
    version: '1.0.0',
    releaseDate: 'June 2026',
    releaseDateDE: 'Juni 2026',
    downloadUrl: '/products/text-animations.zip',
    description: '5+ professionelle Text-Animations-Presets (.ffx) für After Effects. Perfekt für Intros, Titel und Social Media.',
    descriptionEN: '5+ professional text animation presets (.ffx) for After Effects. Perfect for intros, titles and social media.',
    category: 'After Effects Presets'
  },
  'text-animations-2': {
    id: 'text-animations-2',
    name: '5+ Text Animations 2',
    price: 'Free',
    priceValue: 0,
    isFree: true,
    images: [
      '/products/titel/text-animations-2.jpg',
      { type: 'video', url: '/products/text-animations-2-preview.mp4' }
    ],
    version: '1.0.0',
    releaseDate: 'June 2026',
    releaseDateDE: 'Juni 2026',
    downloadUrl: '/products/text-animations-2.zip',
    description: 'Ein zweites Set mit 5+ Text-Animations-Presets (.ffx) + Projektdatei (.aep) für After Effects. Neue Stile für Intros, Titel und Social Media.',
    descriptionEN: 'A second set of 5+ text animation presets (.ffx) + project file (.aep) for After Effects. New styles for intros, titles and social media.',
    category: 'After Effects Presets'
  },
  'animated-gradients': {
    id: 'animated-gradients',
    name: '10+ Animated Gradients',
    price: 'Free',
    priceValue: 0,
    isFree: true,
    images: [
      '/products/titel/animated-gradients.jpg',
      { type: 'video', url: '/products/animated-gradients-preview.mp4' }
    ],
    version: '1.0.0',
    releaseDate: 'July 2026',
    releaseDateDE: 'Juli 2026',
    downloadUrl: '/products/animated-gradients.zip',
    description: '10+ animierte Farbverlauf-Hintergründe als After-Effects-Projektdatei (.aep). Weiche, loopbare Verläufe — perfekt für Intros, Hintergründe und Social-Media-Videos.',
    descriptionEN: '10+ animated gradient backgrounds as an After Effects project file (.aep). Smooth, loopable gradients — perfect for intros, backgrounds and social media videos.',
    category: 'After Effects Project File'
  }
};

// Media Flow seit 01.10.2026 nicht mehr im Angebot (nicht per Installer
// installierbar), Effect Flow noch nicht fertig (altes Artwork, „Bald“) und
// soll nicht gefunden werden. Daten bleiben oben stehen, falls sie (zurueck)kommen.
const AUSGEBLENDET = new Set(['media-flow', 'effect-flow']);
// Umbenannt im Oktober 2026 — alte Adressen leiten auf die neuen weiter.
const UMBENANNT = { 'graph-flow': 'instant-curve', 'composer-flow': 'instant-comp', 'look-flow': 'instant-grade' };
// Rückkehr aus der Abo-Kasse: Stripe schickt auf /shop/abo-jahr bzw. /shop/abo-monat (server.py,
// success_url/cancel_url = /shop/{product_id}). Eine Seite dafür gibt es nicht — weiter zur
// All-in-One-Seite, mit Query; die Meldung zeigt dort KaufHinweis.
const ABO_RUECKKEHR = new Set(['abo-jahr', 'abo-monat']);

const ProductDetail = () => {
  const { productId } = useParams();
  const transitionTo = useTransitionNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { addToCart, openCart } = useCart();

  const isEnglish = i18n.language === 'en' || location.pathname.startsWith('/en');
  const langPrefix = isEnglish ? '/en' : '';
  const [downloadStatus, setDownloadStatus] = useState(null);

  // Aus dem Angebot genommen: alte Links landen im Shop statt auf einer
  // Produktseite, auf der man noch kaufen koennte.
  const ausgeblendet = AUSGEBLENDET.has(productId);
  const product = ausgeblendet ? null : productsData[productId];

  // Structured data so the shop entry can show price + availability directly
  // in the Google result instead of a bare blue link. Coming-soon items have
  // no price yet, and an Offer without one is invalid — those ship the
  // Product node alone. No ratings here on purpose: we don't have real ones.
  const productSchema = useMemo(() => {
    if (!product) return null;
    const url = `https://www.davidmedia.de${langPrefix}/shop/${productId}`;
    /* schema.org will eine VOLLSTAENDIGE Adresse. Die Galerie fuehrt die
       Bilder als "/products/…", damit sie lokal und hinter jeder Domain
       funktionieren — als Strukturdatum ist das aber wertlos, Google kann eine
       relative Adresse nicht aufloesen und zeigt dann gar kein Produktbild. */
    const image = absUrl(typeof product.images?.[0] === 'string' ? titelPfad(product.images[0], isEnglish) : undefined);
    const node = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: t(`products.${productId}.shortDescription`),
      sku: product.id,
      brand: { '@type': 'Brand', name: 'DAVIDMEDIA' },
      url,
    };
    if (image) node.image = image;
    if (typeof product.priceValue === 'number') {
      node.offers = {
        '@type': 'Offer',
        url,
        price: product.priceValue.toFixed(2),
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'David Media' },
      };
    }
    const crumbs = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `https://www.davidmedia.de${langPrefix || '/'}` },
        { '@type': 'ListItem', position: 2, name: 'Shop', item: `https://www.davidmedia.de${langPrefix}/shop` },
        { '@type': 'ListItem', position: 3, name: product.name, item: url }
      ],
    };
    return [node, crumbs];
  }, [product, productId, langPrefix, t]);

  useEffect(() => {
    const download = searchParams.get('download');
    const status = searchParams.get('status');
    if (download === 'success' || status === 'success') {
      setDownloadStatus('success');
    }
  }, [searchParams]);

  if (!product && ausgeblendet) {
    return <Navigate to={`${langPrefix}/shop`} replace />;
  }
  if (!product && UMBENANNT[productId]) {
    return <Navigate to={`${langPrefix}/shop/${UMBENANNT[productId]}`} replace />;
  }
  if (ABO_RUECKKEHR.has(productId)) {
    // Die Rückkehr-Adresse ist immer deutsch: gemerkte Sprache aus der Kasse geht vor
    const gemerkt = gemerkteKaufSprache();
    const prefix = gemerkt ? (gemerkt === 'en' ? '/en' : '') : langPrefix;
    return <Navigate to={`${prefix}/shop/all-in-one${location.search}`} replace />;
  }

  // Unbekannte Adresse unter /shop/…: die normale 404-Seite mit Leiste und Footer
  if (!product) return <NotFound />;

  // Produkte, die es nur im Abo gibt, laufen nie ueber den Warenkorb — Stripe
  // mischt Abonnement und Einmalzahlung nicht. Ihre Knoepfe („7 Tage kostenlos
  // testen“) oeffnen in der Produktseite die Zustimmung vor der Abo-Kasse
  // (kauf/AboZustimmung). Landet so ein Produkt trotzdem hier, geht es zur
  // All-in-One-Seite.
  const zumAbo = () => transitionTo(`${langPrefix}/shop/all-in-one`, 'down');

  const handleAddToCart = () => {
    if (product.aboProdukt) { zumAbo(); return; }
    // Rabattcodes traegt man im Warenkorb ein (die neue Seite hat kein Code-Feld mehr)
    addToCart(product, 'single', 1, null);
    openCart();
  };

  const handleDownload = () => {
    if (product.priceValue === 0 && product.downloadUrl) {
      setDownloadStatus('success');
      // Fire-and-forget tracking — never block the download on it.
      // No PII sent; backend logs product_id + timestamp + UA.
      // Guard against undefined API_URL (local dev without env file)
      // so we don't POST to 'undefined/api/...'.
      if (API_URL) {
        fetch(`${API_URL}/api/products/${productId}/track-download`, {
          method: 'POST',
        }).catch(() => {});
      }
      window.open(product.downloadUrl, '_blank');
    }
  };

  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title={`${product.name} | DAVIDMEDIA`}
        description={t(`products.${productId}.shortDescription`)}
        url={`https://www.davidmedia.de${langPrefix}/shop/${productId}`}
        // Absolut, nicht relativ: WhatsApp, LinkedIn & Co. holen das Bild von
        // ihren eigenen Servern und koennen "/products/…" nicht aufloesen —
        // die Vorschau bliebe ohne Bild. Dasselbe galt fuer die Strukturdaten.
        image={absUrl(typeof product.images?.[0] === 'string' ? titelPfad(product.images[0], isEnglish) : undefined)}
        type="product"
        schema={productSchema}
      />
      <FloatingHeader />
      {/* Rückkehr aus einer Einzelkasse (?status=success / cancelled). Gratis-Pakete melden sich selbst
          („Download gestartet.“), bezahlte bestätigt sonst nichts sichtbar. */}
      {!product.isFree && <KaufHinweis art="einzel" en={isEnglish} produktName={product.name} />}
      <Produktseite
        key={productId}
        id={productId}
        produkt={product}
        en={isEnglish}
        alleProdukte={productsData}
        onWarenkorb={handleAddToCart}
        onDownload={handleDownload}
        geladen={downloadStatus === 'success'}
      />
      <div className="relative z-10">
        <Footer />
      </div>
    </motion.div>
  );
};

export default ProductDetail;
